<template>
    <footer class="bg-footer-bg text-white px-6 py-11">
        <div class="mx-auto max-w-screen-xl flex flex-col items-center gap-2 text-center">
            <p class="text-lg font-black flex items-center gap-2.5">
                <!-- the mark reversed out: the footer is solid primary -->
                <img src="/logo-mark.svg" alt="" aria-hidden="true"
                     width="319" height="329"
                     class="h-7 w-auto brightness-0 invert opacity-90">
                {{ t('nav.title') }}
            </p>
            <p class="text-[15px] text-white/90">
                {{ t('footer.tagline') }}
            </p>
            <!-- The build, on a tooltip. Nothing is added to the page: the
              -- stamp is already in every page's <head> and at /build.json,
              -- and this only makes it reachable by hovering rather than by
              -- opening devtools. Which matters because the thing you usually
              -- want to know is whether staging and production are answering
              -- from the SAME build -- see the deploy trap in CLAUDE.md, where
              -- a deploy with no `make release` before it reports success and
              -- serves the previous image. -->
            <p class="text-sm text-white/70" :title="buildStamp">
                {{ t('footer.edition', { formFactor: wurflData.formFactor }) }}
            </p>

            <!-- Legal pages are plain HTML in public/, so they are served
              -- directly at these paths without needing a router.
              --
              -- Only "what it's for" has translated copies so far, so only that
              -- one follows the reader's language. The other three are English
              -- whatever the footer around them says: a link to a page that
              -- does not exist would be worse than an untranslated one. -->
            <p class="text-sm mt-2.5 flex flex-wrap justify-center gap-x-3 gap-y-1">
                <a :href="whatForHref" class="underline underline-offset-2 hover:text-white">{{ t('footer.whatFor') }}</a>
                <span class="text-white/40" aria-hidden="true">&middot;</span>
                <a href="/image-formats.html" class="underline underline-offset-2 hover:text-white">{{ t('footer.formats') }}</a>
                <span class="text-white/40" aria-hidden="true">&middot;</span>
                <a href="/contact.html" class="underline underline-offset-2 hover:text-white">{{ t('footer.contact') }}</a>
                <span class="text-white/40" aria-hidden="true">&middot;</span>
                <a href="/privacy.html" class="underline underline-offset-2 hover:text-white">{{ t('footer.privacy') }}</a>
                <span class="text-white/40" aria-hidden="true">&middot;</span>
                <a href="/terms.html" class="underline underline-offset-2 hover:text-white">{{ t('footer.terms') }}</a>
            </p>

            <!-- THE SAME HOOK THE OTHER NINE FOOTERS CARRY, filled by
              -- public/youtube.js. A <span> rather than a Vue component
              -- because the link has to be identical on the application, on
              -- a task page, on a walkthrough and on a legal page, and only
              -- one of those four tiers can mount a component. -->
            <p class="mt-2"><span data-youtube="foot"></span></p>

            <p class="text-[13px] text-white/70 mt-2">
                {{ t('footer.copyright', { year: currentYear }) }}
            </p>
        </div>
    </footer>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

/* The footer was in Italian and pointed at the English page.
 *
 * content-pages.json still carries a translation for every language in
 * SUPPORTED, so the path CAN be derived -- but a translation existing in the
 * registry is no longer the same as a page existing on disk. Only the languages
 * in window.OIOLang.published get one built, so that is what decides, and
 * everything else falls back to the hand-written English page rather than
 * linking somewhere that 404s.
 *
 * The same two lines live in App.vue; a shared helper for one expression would
 * be indirection, and the same test covers both. */
const whatForHref = computed(() => {
    const shipped = (typeof window !== "undefined" && window.OIOLang
                     && window.OIOLang.published) || ["en"]
    return shipped.includes(locale.value) && locale.value !== "en"
        ? `/${locale.value}/what-is-it-for.html`
        : "/what-is-it-for.html"
})

defineProps({
	wurflData: { type: Object, required: true },
})

// computed once at load rather than hardcoded, so the notice cannot go stale
// again the way "2023" did
/* Read out of the page rather than injected at compile time.
 *
 * tools/make_build_stamp.py writes the tag into the built HTML after every
 * generator has run, so this is the same string the standalone pages and
 * /build.json carry. A define replaced by the bundler would be a second
 * source, and the two would disagree the first time a page was regenerated
 * without a rebuild -- which is exactly the failure the stamp exists to catch.
 *
 * Empty in dev, where nothing has stamped anything: a title of "" renders no
 * tooltip at all, which is the right nothing.
 */
const buildStamp = (() => {
    try {
        const el = document.querySelector('meta[name="build"]')
        return (el && el.getAttribute("content")) || ""
    } catch (err) {
        return ""
    }
})()

const currentYear = new Date().getFullYear()

</script>
